import { Controller } from "@hotwired/stimulus"

// Google-Calendar-style cards for the Calendar grids. Clicking an event chip
// opens a small card next to it (title, when, where, a join button, guests)
// instead of going straight to the right-hand panel; "+N more" opens the day's
// events as a list. The card's Details button carries the chip's
// data-detail-url / -key, so the existing event-detail controller (an ancestor,
// listening for clicks) opens the full panel from it. Everything is built with
// textContent, never HTML: event text is someone else's input.
const EVENT = "[data-popover-title]"

export default class extends Controller {
    connect() {
        this.card = document.createElement("div")
        this.card.className = "cal-popover"
        this.card.setAttribute("role", "dialog")
        this.card.hidden = true
        this.element.appendChild(this.card)

        this.onClick = (event) => this.handleClick(event)
        this.onKey = (event) => {
            if (event.key === "Escape" && !this.card.hidden) {
                event.stopPropagation()
                this.close()
            }
        }
        this.element.addEventListener("click", this.onClick, true)
        // composedPath, not contains(target): picking a row of the day list
        // replaces the card's children while the click is still travelling, so
        // by the time it reaches document the row is no longer in the tree and
        // the click would look like one outside.
        document.addEventListener("click", this.onOutside = (event) => {
            if (!event.composedPath().includes(this.element)) this.close()
        })
        document.addEventListener("keydown", this.onKey, true)
        // event-detail stops the Details click in the capture phase, before the
        // button could hear it; document-level capture runs first of all.
        document.addEventListener("click", this.onDetails = (event) => {
            if (event.target.closest?.(".cal-popover [data-detail-url]")) this.close()
        }, true)
    }

    disconnect() {
        this.element.removeEventListener("click", this.onClick, true)
        document.removeEventListener("click", this.onOutside)
        document.removeEventListener("keydown", this.onKey, true)
        document.removeEventListener("click", this.onDetails, true)
        this.card?.remove()
    }

    handleClick(event) {
        if (this.card.contains(event.target)) return

        let chip = event.target.closest(EVENT)
        if (chip && this.element.contains(chip)) {
            event.preventDefault()
            event.stopPropagation()
            if (this.chip === chip && !this.card.hidden) return this.close()
            return this.showEvent(chip, chip.getBoundingClientRect())
        }

        let more = event.target.closest(".cal-more")
        if (more && this.element.contains(more)) {
            event.preventDefault()
            event.stopPropagation()
            return this.showDay(more.closest(".cal-day"), more.getBoundingClientRect())
        }

        this.close()
    }

    close() {
        if (this.card.hidden) return
        this.card.hidden = true
        this.chip?.classList.remove("is-selected")
        this.chip = null
    }

    // --- Event card ---------------------------------------------------------

    showEvent(chip, anchor) {
        this.chip?.classList.remove("is-selected")
        this.chip = chip
        chip.classList.add("is-selected")
        let data = chip.dataset

        let card = this.card
        card.replaceChildren()
        card.dataset.tone = data.popoverTone || "internal"

        let bar = this.el("div", "cal-popover-bar")
        let details = this.el("button", "cal-popover-icon")
        details.type = "button"
        details.title = "Details"
        details.setAttribute("aria-label", "Details")
        details.appendChild(this.icon("bi-layout-sidebar-inset-reverse"))
        // The hooks event-detail reads; clicking bubbles to it.
        details.dataset.detailUrl = data.popoverDetailUrl
        details.dataset.detailKey = data.popoverDetailKey
        details.dataset.eventPageUrl = data.popoverPageUrl
        bar.appendChild(details)
        if (data.popoverLink) {
            let open = this.el("a", "cal-popover-icon")
            open.href = data.popoverLink
            open.target = "_blank"
            open.rel = "noopener noreferrer"
            open.title = "Open in Google Calendar"
            open.setAttribute("aria-label", "Open in Google Calendar")
            open.appendChild(this.icon("bi-box-arrow-up-right"))
            bar.appendChild(open)
        }
        let close = this.el("button", "cal-popover-icon")
        close.type = "button"
        close.title = "Close"
        close.setAttribute("aria-label", "Close")
        close.appendChild(this.icon("bi-x-lg"))
        close.addEventListener("click", () => this.close())
        bar.appendChild(close)
        card.appendChild(bar)

        let title = this.el("h3", "cal-popover-title")
        title.textContent = data.popoverTitle
        card.appendChild(title)
        card.appendChild(this.line("bi-clock", data.popoverWhen))
        if (data.popoverLocation) card.appendChild(this.line("bi-geo-alt", data.popoverLocation))
        let guests = Number(data.popoverGuests || 0)
        if (guests > 0) card.appendChild(this.line("bi-people", `${guests} ${guests === 1 ? "guest" : "guests"}`))
        if (data.popoverVideo) {
            let join = this.el("a", "cal-popover-join")
            join.href = data.popoverVideo
            join.target = "_blank"
            join.rel = "noopener noreferrer"
            join.appendChild(this.icon("bi-camera-video-fill"))
            join.appendChild(document.createTextNode(" Join video call"))
            card.appendChild(join)
        }
        this.place(anchor)
    }

    // --- Day card: every event of one day, as a list -----------------------

    showDay(cell, anchor) {
        if (!cell) return
        this.chip?.classList.remove("is-selected")
        this.chip = null
        let card = this.card
        card.replaceChildren()
        card.dataset.tone = "day"

        let bar = this.el("div", "cal-popover-bar")
        let close = this.el("button", "cal-popover-icon")
        close.type = "button"
        close.title = "Close"
        close.setAttribute("aria-label", "Close")
        close.appendChild(this.icon("bi-x-lg"))
        close.addEventListener("click", () => this.close())
        bar.appendChild(close)
        card.appendChild(bar)

        let label = cell.querySelector(".cal-day-number")?.getAttribute("aria-label") || ""
        let title = this.el("h3", "cal-popover-title")
        title.textContent = label
        card.appendChild(title)

        let list = this.el("div", "cal-popover-list")
        cell.querySelectorAll(EVENT).forEach((chip) => {
            let row = this.el("button", "cal-popover-row")
            row.type = "button"
            row.dataset.tone = chip.dataset.popoverTone || "internal"
            let time = this.el("span", "cal-popover-row-time")
            time.textContent = chip.dataset.popoverTime || "All day"
            let name = this.el("span", "cal-popover-row-title")
            name.textContent = chip.dataset.popoverTitle
            row.append(time, name)
            row.addEventListener("click", () => this.showEvent(chip, anchor))
            list.appendChild(row)
        })
        card.appendChild(list)
        this.place(anchor)
    }

    // --- Pieces -------------------------------------------------------------

    el(tag, className) {
        let node = document.createElement(tag)
        node.className = className
        return node
    }

    icon(name) {
        let node = document.createElement("i")
        node.className = `bi ${name}`
        node.setAttribute("aria-hidden", "true")
        return node
    }

    line(iconName, text) {
        let row = this.el("div", "cal-popover-line")
        row.appendChild(this.icon(iconName))
        let span = document.createElement("span")
        span.textContent = text
        row.appendChild(span)
        return row
    }

    // Beside the anchor when there is room on the right, else on its left; kept
    // inside the window either way.
    place(anchor) {
        let card = this.card
        card.hidden = false
        card.style.left = "0px"
        card.style.top = "0px"
        let { width, height } = card.getBoundingClientRect()
        let margin = 8
        let left = anchor.right + margin
        if (left + width > window.innerWidth - margin) left = anchor.left - width - margin
        if (left < margin) left = Math.max(margin, Math.min(anchor.left, window.innerWidth - width - margin))
        let top = Math.max(margin, Math.min(anchor.top, window.innerHeight - height - margin))
        card.style.left = `${Math.round(left)}px`
        card.style.top = `${Math.round(top)}px`
        card.focus({ preventScroll: true })
    }
}
